// Bucket status validation errors
export const bucketErrorCode = {
    ALREADY_ENDED: "bucket_already_ended",
    ALREADY_STARTED: "bucket_already_started",
    FIXED_START_NOT_REACHED: "bucket_fixed_start_not_reached",
    PLANNED_END_EXCEEDED: "bucket_planned_end_exceeded",
    NOT_YET_STARTED: "bucket_not_yet_started",
    CONCURRENT_IN_PROGRESS: "bucket_concurrent_in_progress",
    EARLIER_BUCKET_EXISTS: "bucket_earlier_bucket_exists",
    // Batch creation
    TIME_CONFLICT: "bucket_time_conflict",
    // Deletion
    DELETE_NOT_ALLOWED: "bucket_delete_not_allowed",
    // Editing
    EDIT_NOT_ALLOWED: "bucket_edit_not_allowed",
    // Completion
    HAS_INCOMPLETE_TASKS: "bucket_has_incomplete_tasks",
};
// Generic permission / resource error codes (shared across modules)
export const commonErrorCode = {
    UNAUTHORIZED: "unauthorized",
    FORBIDDEN: "forbidden",
    NOT_FOUND: "not_found",
    CONFLICT: "conflict",
    VALIDATION_FAILED: "validation_failed",
    INTERNAL_ERROR: "internal_error",
    NOT_IMPLEMENTED: "not_implemented",
};
// Step/task workflow errors
export const workflowErrorCode = {
    STEP_NOT_FOUND: "step_not_found",
    STEP_TASK_MISMATCH: "step_task_mismatch",
    STEP_TYPE_MISMATCH: "step_type_mismatch",
    STEP_ALREADY_COMPLETED: "step_already_completed",
    UNKNOWN_STEP_TYPE: "unknown_step_type",
    TASK_NOT_FOUND: "task_not_found",
    TASK_ASSIGNEE_MISMATCH: "task_assignee_mismatch",
    TASK_BUCKET_MISMATCH: "task_bucket_mismatch",
    TASK_ALREADY_TERMINAL: "task_already_terminal",
    BUCKET_NOT_FOUND: "bucket_not_found",
    BUCKET_ASSIGNEE_MISMATCH: "bucket_assignee_mismatch",
    BUCKET_NOT_IN_PROGRESS: "bucket_not_in_progress",
    BUCKET_ALREADY_STARTED: "bucket_already_started",
    BUCKET_ALREADY_TERMINAL: "bucket_already_terminal",
    INVALID_BUCKET_TYPE: "invalid_bucket_type",
    INBOX_INFERENCE_FAILED: "inbox_inference_failed",
    TASK_ALREADY_IN_PROGRESS: "task_already_in_progress",
    IMPLEMENTATION_MISSING: "implementation_missing",
    INVALID_TRANSITION_PAYLOAD: "invalid_transition_payload",
    // Invoice task-level claiminiId lock — once the first entry is committed the
    // task's claiminiId is fixed and "create incident" is no longer allowed.
    INVOICE_CLAIMINI_ID_LOCKED: "invoice_claimini_id_locked",
    INVOICE_CREATE_INCIDENT_NOT_ALLOWED: "invoice_create_incident_not_allowed",
    INVOICE_INCIDENT_NOT_LOCKED: "invoice_incident_not_locked",
    // Committed step data didn't parse — lock can't be determined. Unrecoverable; needs manual repair.
    INVOICE_CLAIMINI_ID_LOCK_MISSING: "invoice_claimini_id_lock_missing",
    INVOICE_LINK_PERSIST_FAILED: "invoice_link_persist_failed",
    // POST /tasks/email defense layer: an invoice task is only accepted when at least one
    // attachment's highest-confidence document category is DOCUMENT
    INVOICE_INVALID_ATTACHMENT: "invoice_invalid_attachment",
    // POST /tasks/incident-report idempotency: an incident_report task already exists for this incidentClaiminiId
    INCIDENT_REPORT_ALREADY_EXISTS: "incident_report_already_exists",
    // POST /tasks/delete: task type is not a document task type (isDocumentTaskType)
    TASK_NON_DOCUMENT_TYPE: "task_non_document_type",
    // POST /tasks/delete: the passed stepId isn't the task's actual open step (stale client view)
    STEP_NO_LONGER_CURRENT: "step_no_longer_current",
};
// External service HTTP error codes
export const externalServiceErrorCode = {
    // POST /tasks/email setMailOwner failure (route-level response codes)
    MESSAGE_MANAGER_SET_OWNER_FAILED: "message_manager_set_owner_failed",
    MESSAGE_MANAGER_SET_OWNER_NOT_FOUND: "message_manager_set_owner_not_found",
    MESSAGE_MANAGER_SET_OWNER_BAD_REQUEST: "message_manager_set_owner_bad_request",
    // MessageManagerService — outcome codes shared across all operations
    MESSAGE_MANAGER_NOT_FOUND: "message_manager_not_found",
    MESSAGE_MANAGER_BAD_REQUEST: "message_manager_bad_request",
    MESSAGE_MANAGER_SERVER_ERROR: "message_manager_server_error",
    // Special: has a user-facing translation in the web app
    MESSAGE_MANAGER_UNASSIGN_FAILED: "message_manager_unassign_failed",
    // ClaiminiAdapterService.patchContainer
    CLAIMINI_ADAPTER_PATCH_NOT_FOUND: "claimini_adapter_patch_not_found",
    CLAIMINI_ADAPTER_PATCH_BAD_REQUEST: "claimini_adapter_patch_bad_request",
    CLAIMINI_ADAPTER_PATCH_UNAUTHORIZED: "claimini_adapter_patch_unauthorized",
    CLAIMINI_ADAPTER_PATCH_FORBIDDEN: "claimini_adapter_patch_forbidden",
    CLAIMINI_ADAPTER_PATCH_SERVER_ERROR: "claimini_adapter_patch_server_error",
    // ClaiminiAdapterService.getGuidelines / getContainer
    CLAIMINI_ADAPTER_GET_NOT_FOUND: "claimini_adapter_get_not_found",
    CLAIMINI_ADAPTER_GET_UNAUTHORIZED: "claimini_adapter_get_unauthorized",
    CLAIMINI_ADAPTER_GET_FORBIDDEN: "claimini_adapter_get_forbidden",
    CLAIMINI_ADAPTER_GET_SERVER_ERROR: "claimini_adapter_get_server_error",
    // A field in the response has a shape ePort does not expect
    CLAIMINI_ADAPTER_GET_INVALID_RESPONSE: "claimini_adapter_get_invalid_response",
    // ClaiminiAdapterService.createIncident (POST /eport/v1/incidents)
    CLAIMINI_ADAPTER_POST_BAD_REQUEST: "claimini_adapter_post_bad_request",
    CLAIMINI_ADAPTER_POST_UNAUTHORIZED: "claimini_adapter_post_unauthorized",
    CLAIMINI_ADAPTER_POST_FORBIDDEN: "claimini_adapter_post_forbidden",
    CLAIMINI_ADAPTER_POST_NOT_FOUND: "claimini_adapter_post_not_found",
    CLAIMINI_ADAPTER_POST_SERVER_ERROR: "claimini_adapter_post_server_error",
    // DocumentManagerService.bulkIngest
    DOCUMENT_MANAGER_BULK_INGEST_BAD_REQUEST: "document_manager_bulk_ingest_bad_request",
    DOCUMENT_MANAGER_BULK_INGEST_UNAUTHORIZED: "document_manager_bulk_ingest_unauthorized",
    DOCUMENT_MANAGER_BULK_INGEST_FORBIDDEN: "document_manager_bulk_ingest_forbidden",
    DOCUMENT_MANAGER_BULK_INGEST_NOT_FOUND: "document_manager_bulk_ingest_not_found",
    DOCUMENT_MANAGER_BULK_INGEST_SERVER_ERROR: "document_manager_bulk_ingest_server_error",
    // S3AdapterService.resolveStableFileUrl
    S3_ADAPTER_RESOLVE_BAD_REQUEST: "s3_adapter_resolve_bad_request",
    S3_ADAPTER_RESOLVE_UNAUTHORIZED: "s3_adapter_resolve_unauthorized",
    S3_ADAPTER_RESOLVE_FORBIDDEN: "s3_adapter_resolve_forbidden",
    S3_ADAPTER_RESOLVE_NOT_FOUND: "s3_adapter_resolve_not_found",
    S3_ADAPTER_RESOLVE_SERVER_ERROR: "s3_adapter_resolve_server_error",
    S3_ADAPTER_RESOLVE_INVALID_RESPONSE: "s3_adapter_resolve_invalid_response",
};
